import { useMemo, useState } from 'react'
import { Link } from 'react-router'
import { Modal, Button, Input, Textarea } from '../ui'
import { templatesApiService, type PromoteResponse } from '../../services/templatesApiService'
import { ApiError } from '../../services/BaseApiService'
import { colors, fontSize, radius, spacing } from '../../styles/theme'
import { datetimeLocalToIso, toDatetimeLocalValue } from '../../utils/localDateTime'
import type { ApprovalLevel } from '../../types'

interface PromoteToLiveModalProps {
  version: { templateVersionId: string; versionNumber: number; concurrencyToken?: string | null }
  /** Version number currently live (null: none; undefined: not known here). It is replaced. */
  liveVersionNumber?: number | null
  /** Effective promotion-approval level. self/dual raise a request instead of promoting. */
  approvalLevel?: ApprovalLevel
  onClose: () => void
  onDone: (result: PromoteResponse) => void
}

/**
 * Promotes one version to the Live workspace, now or at a scheduled local time. Exactly one
 * version per template is live, so this replaces the current live version.
 */
export default function PromoteToLiveModal({ version, liveVersionNumber, approvalLevel = 'auto', onClose, onDone }: PromoteToLiveModalProps) {
  const approvalRequired = approvalLevel !== 'auto'
  const [mode, setMode] = useState<'now' | 'schedule'>('now')
  const [when, setWhen] = useState('')
  const [changeNote, setChangeNote] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  // Set once a request was raised; the modal stays open to say so, and reports on close.
  const [requested, setRequested] = useState<PromoteResponse | null>(null)
  const minWhen = useMemo(() => toDatetimeLocalValue(new Date()), [])

  async function handleConfirm() {
    setError('')
    let promoteAt: string | null = null
    if (mode === 'schedule') {
      promoteAt = datetimeLocalToIso(when)
      if (!promoteAt) { setError('Choose a date and time.'); return }
      if (new Date(when).getTime() <= Date.now()) { setError('Choose a time in the future.'); return }
    }
    setLoading(true)
    try {
      const result = await templatesApiService.promote(version.templateVersionId, {
        changeNote: changeNote.trim() || undefined,
        promoteAt,
        concurrencyToken: version.concurrencyToken ?? undefined,
      })
      if ('approvalRequired' in result && result.approvalRequired) {
        setRequested(result)
        return
      }
      onDone(result)
      onClose()
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setError('This template was changed by another request. Close and try again.')
      } else if (err instanceof ApiError && err.status === 403) {
        setError('You do not have permission to promote this template.')
      } else {
        setError(err instanceof ApiError ? err.message : 'Promotion failed')
      }
    } finally {
      setLoading(false)
    }
  }

  function finish() {
    if (requested) onDone(requested)
    onClose()
  }

  if (requested && 'approvalRequired' in requested) {
    const self = (requested.approvalLevel ?? approvalLevel) === 'self'
    return (
      <Modal
        open
        onClose={finish}
        title={`Promote v${version.versionNumber} to Live`}
        width={480}
        footer={<Button variant="primary" onClick={finish}>Close</Button>}
      >
        <div role="status" style={{
          background: colors.successLight, color: colors.success,
          border: `1px solid ${colors.successBorder}`, borderRadius: radius.sm,
          padding: `${spacing.sm} ${spacing.md}`, fontSize: fontSize.sm,
        }}>
          {self
            ? <>Request created — approve it from the link in your email or on the{' '}
              {/* Leaving the page: skip onDone, whose navigation would win over this link. */}
              <Link to={`/app/approvals?request=${encodeURIComponent(requested.promotionRequestId)}`} onClick={onClose}
                style={{ color: colors.primary, fontWeight: 600 }}>Approvals page</Link>.</>
            : 'Approval requested — approvers have been notified.'}
        </div>
      </Modal>
    )
  }

  const confirmLabel = approvalRequired
    ? 'Request approval'
    : mode === 'now' ? 'Promote now' : 'Schedule promotion'

  const radioRow: React.CSSProperties = {
    display: 'flex', alignItems: 'center', gap: spacing.xs,
    fontSize: fontSize.sm, color: colors.text, cursor: 'pointer',
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={approvalRequired ? `Request approval to promote v${version.versionNumber}` : `Promote v${version.versionNumber} to Live`}
      width={480}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={loading}>Cancel</Button>
          <Button variant="primary" onClick={handleConfirm} loading={loading}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>
        <div style={{ fontSize: fontSize.sm, color: colors.textSecondary, lineHeight: 1.5 }}>
          Only one version of a template can be live.{' '}
          {liveVersionNumber === undefined
            ? <>This replaces the current live version, if there is one.</>
            : liveVersionNumber !== null
              ? <>This replaces the current live version, <strong>v{liveVersionNumber}</strong>.</>
              : <>No version is live yet.</>}
        </div>

        {approvalRequired && (
          <div data-testid="approval-required-note" style={{
            background: colors.warningLight, border: `1px solid ${colors.warningBorder}`, borderRadius: radius.sm,
            padding: `${spacing.sm} ${spacing.md}`, fontSize: fontSize.sm, color: colors.text, lineHeight: 1.5,
          }}>
            {approvalLevel === 'self'
              ? <>You'll get an email with a link to approve this yourself. The approval is recorded for audit.</>
              : <>Promoting this template requires approval from someone else with Manage on the folder. This sends a
                request to the approvers; once approved, the version goes live now or at the time you schedule below.</>}
          </div>
        )}

        {error && (
          <div role="alert" style={{
            background: colors.errorLight, color: colors.error,
            border: `1px solid ${colors.errorBorder}`, borderRadius: radius.sm,
            padding: `${spacing.sm} ${spacing.md}`, fontSize: fontSize.sm,
          }}>{error}</div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs }}>
          <label style={radioRow}>
            <input type="radio" name="promote-when" checked={mode === 'now'} onChange={() => setMode('now')} />
            {approvalRequired ? 'Go live as soon as approved' : 'Promote now'}
          </label>
          <label style={radioRow}>
            <input type="radio" name="promote-when" checked={mode === 'schedule'} onChange={() => setMode('schedule')} />
            {approvalRequired ? 'Schedule for later (applies once approved)' : 'Schedule for later'}
          </label>
        </div>

        {mode === 'schedule' && (
          <Input
            type="datetime-local"
            label="Go live at (your local time)"
            aria-label="Go live at"
            value={when}
            min={minWhen}
            onChange={e => setWhen(e.target.value)}
          />
        )}

        <Textarea
          label="Change note"
          aria-label="Change note"
          placeholder="What changed in this version (required if your team requires change notes)"
          value={changeNote}
          onChange={e => setChangeNote(e.target.value)}
          rows={3}
        />
      </div>
    </Modal>
  )
}
